import { ScrollArea } from '@zond/ui';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAppName, getLogo } from '../../../utils/get-logo';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { t } = useTranslation('legal');

  return (
    <div className="container mx-auto py-8 px-4 md:py-12 h-screen overflow-hidden">
      <div className="max-w-3xl mx-auto h-full flex flex-col">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <img src={getLogo()} alt={t('logoAlt')} className="h-6 w-6" />
            <span className="font-medium">{getAppName()}</span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="rounded-full p-2 hover:bg-muted transition-colors"
            aria-label={t('common:actions.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <h1 className="text-3xl font-bold mb-8">{t('privacy.title')}</h1>

        <div className="overflow-y-auto custom-scrollbar">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.introduction')}</h2>
              <p className="mb-4">
                At Klarops, we take your privacy seriously. This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you use our service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.informationWeCollect')}</h2>
              <h3 className="text-xl font-medium mb-3">{t('privacy.sections.personalInformation')}</h3>
              <p className="mb-4">We collect information that you provide directly to us, including:</p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Name and contact information</li>
                <li className="mb-2">Account credentials</li>
                <li className="mb-2">Profile information</li>
                <li className="mb-2">Payment information</li>
              </ul>

              <h3 className="text-xl font-medium mb-3">{t('privacy.sections.usageInformation')}</h3>
              <p className="mb-4">
                We automatically collect certain information about your device and how you interact with our
                Service, including:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Device information</li>
                <li className="mb-2">Log data</li>
                <li className="mb-2">Usage patterns</li>
                <li className="mb-2">Performance data</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.howWeUse')}</h2>
              <p className="mb-4">We use the information we collect for various purposes, including:</p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Providing and maintaining our Service</li>
                <li className="mb-2">Improving and personalizing user experience</li>
                <li className="mb-2">Processing your transactions</li>
                <li className="mb-2">Communicating with you</li>
                <li className="mb-2">Ensuring security and preventing fraud</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.informationSharing')}</h2>
              <p className="mb-4">
                We do not sell your personal information. We may share your information in the following
                circumstances:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">With your consent</li>
                <li className="mb-2">With service providers</li>
                <li className="mb-2">For legal compliance</li>
                <li className="mb-2">To protect rights and safety</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.dataSecurity')}</h2>
              <p className="mb-4">
                We implement appropriate technical and organizational measures to protect your personal
                information against unauthorized access, alteration, disclosure, or destruction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.yourRights')}</h2>
              <p className="mb-4">You have certain rights regarding your personal information, including:</p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Access to your data</li>
                <li className="mb-2">Correction of inaccurate data</li>
                <li className="mb-2">Deletion of your data</li>
                <li className="mb-2">Objection to processing</li>
                <li className="mb-2">Data portability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.cookies')}</h2>
              <p className="mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our platform.
                You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.childrensPrivacy')}</h2>
              <p className="mb-4">
                Our Service is not intended for children under 13 years of age. We do not knowingly collect
                personal information from children under 13.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.changes')}</h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by
                posting the new Privacy Policy on this page and updating the "Last Updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">{t('privacy.sections.contact')}</h2>
              <p className="mb-4">
                If you have any questions about this Privacy Policy, please contact us at privacy@klarops.com
              </p>
            </section>

            <div className="text-sm text-muted-foreground mt-8">
              {t('lastUpdated', { date: 'March 2024' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
